/**
 * Production Logging System
 *
 * Comprehensive logging with:
 * - Structured logging with context
 * - Multiple log levels and filtering
 * - Remote logging to monitoring services
 * - Log aggregation and buffering
 * - Sensitive data sanitization
 * - Performance-optimized logging
 * - Log sampling and rate limiting
 */


export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

export interface LogEntry {
  id: string;
  timestamp: number;
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
  metadata: {
    url: string;
    userAgent: string;
    userId?: string;
    tenantId?: string;
    sessionId: string;
    buildVersion?: string;
    environment: string;
  };
  performance?: {
    duration?: number;
    memoryUsage?: number;
  };
}

export interface LoggerConfig {
  level: LogLevel;
  enableConsoleOutput: boolean;
  enableRemoteLogging: boolean;
  remoteEndpoint?: string;
  bufferSize: number;
  flushInterval: number;
  sampleRate: number;
  maxRetries: number;
  sanitizeData: boolean;
  enablePerformanceLogging: boolean;
  contextEnhancers: Array<() => Record<string, unknown>>;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  critical: 4
};

const DEFAULT_CONFIG: LoggerConfig = {
  level: 'info',
  enableConsoleOutput: true,
  enableRemoteLogging: true,
  bufferSize: 100,
  flushInterval: 10000, // 10 seconds
  sampleRate: 1.0,
  maxRetries: 3,
  sanitizeData: true,
  enablePerformanceLogging: true,
  contextEnhancers: []
};

export class Logger {
  private config: LoggerConfig;
  private buffer: LogEntry[] = [];
  private sessionId: string;
  private flushTimer: NodeJS.Timeout | null = null;
  private pendingFlush = false;
  private retryQueue: LogEntry[] = [];
  private sensitiveFields = [
    'password', 'token', 'secret', 'key', 'authorization', 
    'cookie', 'session', 'credential', 'auth', 'bearer',
    'ssn', 'social', 'credit', 'card', 'cvv', 'pin'
  ];

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.sessionId = this.generateSessionId();
    this.startPeriodicFlush();
  }

  private generateSessionId(): string {
    return `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  private sanitizeData(data: unknown): unknown {
    if (!this.config.sanitizeData) {
      return data;
    }

    if (data === null || data === undefined) {
      return data;
    }

    if (typeof data === 'string') {
      // Check if string looks like sensitive data
      const lowerData = data.toLowerCase();
      if (this.sensitiveFields.some(field => lowerData.includes(field))) {
        return '[REDACTED]';
      }
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item));
    }

    if (typeof data === 'object') {
      const sanitized: Record<string, unknown> = {};
      Object.keys(data).forEach(key => {
        const lowerKey = key.toLowerCase();
        if (this.sensitiveFields.some(field => lowerKey.includes(field))) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = this.sanitizeData((data as Record<string, unknown>)[key]);
        }
      });
      return sanitized;
    }

    return data;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    error?: Error,
    performance?: { duration?: number }
  ): LogEntry {
    const entry: LogEntry = {
      id: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      timestamp: Date.now(),
      level,
      message,
      context: context ? this.sanitizeData(context) as Record<string, unknown> : undefined,
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : undefined,
      metadata: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: this.getCurrentUserId(),
        tenantId: this.getCurrentTenantId(),
        sessionId: this.sessionId,
        buildVersion: process.env.REACT_APP_VERSION || 'unknown',
        environment: process.env.NODE_ENV || 'unknown'
      }
    };

    // Add performance data
    if (this.config.enablePerformanceLogging) {
      entry.performance = {
        duration: performance?.duration,
        memoryUsage: this.getMemoryUsage()
      };
    }

    // Enhance with additional context
    if (this.config.contextEnhancers.length > 0) {
      const enhancedContext = this.config.contextEnhancers.reduce((acc, enhancer) => {
        try {
          return { ...acc, ...enhancer() };
        } catch {
          return acc;
        }
      }, {});

      if (Object.keys(enhancedContext).length > 0) {
        entry.context = {
          ...entry.context,
          ...this.sanitizeData(enhancedContext) as Record<string, unknown>
        };
      }
    }

    return entry;
  }

  private getCurrentUserId(): string | undefined {
    try {
      const user = JSON.parse(localStorage.getItem('auth_user') || '{}');
      return user.id;
    } catch {
      return undefined;
    }
  }

  private getCurrentTenantId(): string | undefined {
    try {
      const user = JSON.parse(localStorage.getItem('auth_user') || '{}');
      return user.tenantId;
    } catch {
      return undefined;
    }
  }

  private getMemoryUsage(): number | undefined {
    if ('memory' in performance) {
      return (performance as any).memory.usedJSHeapSize;
    }
    return undefined;
  }

  private outputToConsole(entry: LogEntry): void {
    if (!this.config.enableConsoleOutput) {
      return;
    }

    const timestamp = new Date(entry.timestamp).toISOString();
    const prefix = `[${timestamp}] [${entry.level.toUpperCase()}] [${entry.id}]`;
    
    const consoleMethod = entry.level === 'debug' ? console.debug :
                         entry.level === 'info' ? console.info :
                         entry.level === 'warn' ? console.warn :
                         console.error;

    if (entry.error) {
      consoleMethod(`${prefix} ${entry.message}`, entry.context || {}, entry.error);
    } else {
      consoleMethod(`${prefix} ${entry.message}`, entry.context || {});
    }
  }

  private addToBuffer(entry: LogEntry): void {
    this.buffer.push(entry);

    // Flush immediately for critical errors
    if (entry.level === 'critical') {
      this.flush().catch(() => {
        // Silently handle flush errors
      });
    }

    // Maintain buffer size
    if (this.buffer.length >= this.config.bufferSize) {
      this.flush().catch(() => {
        // Silently handle flush errors
      });
    }
  }

  private startPeriodicFlush(): void {
    this.flushTimer = setInterval(() => {
      if (this.buffer.length > 0) {
        this.flush().catch(() => {
          // Silently handle flush errors
        });
      }
    }, this.config.flushInterval);
  }

  private async flush(): Promise<void> {
    if (!this.config.enableRemoteLogging || this.pendingFlush) {
      return;
    }

    const entriesToFlush = [...this.buffer, ...this.retryQueue];
    if (entriesToFlush.length === 0) {
      return;
    }

    this.pendingFlush = true;
    this.buffer = [];
    this.retryQueue = [];

    try {
      // ✅ REPOSITORY PATTERN: Use ErrorReportingRepository instead of direct fetch
      if (this.config.enableRemoteLogging) {
        // Dynamic import to avoid circular dependencies
        const { errorReportingRepository } = await import('../data/repositories/ErrorReportingRepository');

        const tenantId = this.getCurrentTenant();
        await errorReportingRepository.logEntries(entriesToFlush, tenantId);
      }
    } catch (error) {
      // Add failed entries back to retry queue
      this.retryQueue.push(...entriesToFlush);
      
      // Limit retry queue size
      if (this.retryQueue.length > this.config.bufferSize * 2) {
        this.retryQueue = this.retryQueue.slice(-this.config.bufferSize);
      }

      console.warn('Failed to send logs to remote endpoint:', error);
    } finally {
      this.pendingFlush = false;
    }
  }

  public debug(message: string, context?: Record<string, unknown>): void {
    if (!this.shouldLog('debug') || Math.random() > this.config.sampleRate) {
      return;
    }

    const entry = this.createLogEntry('debug', message, context);
    this.outputToConsole(entry);
    this.addToBuffer(entry);
  }

  public info(message: string, context?: Record<string, unknown>): void {
    if (!this.shouldLog('info') || Math.random() > this.config.sampleRate) {
      return;
    }

    const entry = this.createLogEntry('info', message, context);
    this.outputToConsole(entry);
    this.addToBuffer(entry);
  }

  public warn(message: string, context?: Record<string, unknown>): void {
    if (!this.shouldLog('warn')) {
      return;
    }

    const entry = this.createLogEntry('warn', message, context);
    this.outputToConsole(entry);
    this.addToBuffer(entry);
  }

  public error(message: string, error?: Error, context?: Record<string, unknown>): void {
    if (!this.shouldLog('error')) {
      return;
    }

    const entry = this.createLogEntry('error', message, context, error);
    this.outputToConsole(entry);
    this.addToBuffer(entry);
  }

  public critical(message: string, error?: Error, context?: Record<string, unknown>): void {
    if (!this.shouldLog('critical')) {
      return;
    }

    const entry = this.createLogEntry('critical', message, context, error);
    this.outputToConsole(entry);
    this.addToBuffer(entry);
  }

  public logPerformance(
    operation: string,
    duration: number,
    context?: Record<string, unknown>
  ): void {
    if (!this.config.enablePerformanceLogging || !this.shouldLog('info')) {
      return;
    }

    this.info(`Performance: ${operation}`, {
      ...context,
      duration,
      performance: true
    });
  }

  public async timeOperation<T>(
    operation: string,
    fn: () => Promise<T>,
    context?: Record<string, unknown>
  ): Promise<T> {
    const startTime = performance.now();
    
    try {
      const result = await fn();
      const duration = performance.now() - startTime;
      this.logPerformance(operation, duration, { ...context, success: true });
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.error(`${operation} failed`, error as Error, { ...context, duration });
      throw error;
    }
  }

  public createChildLogger(defaultContext: Record<string, unknown>): Logger {
    const childLogger = new Logger(this.config);
    
    // Override context enhancers to include default context
    childLogger.config.contextEnhancers = [
      () => defaultContext,
      ...this.config.contextEnhancers
    ];

    return childLogger;
  }

  public setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  public getBufferSize(): number {
    return this.buffer.length + this.retryQueue.length;
  }

  public async flushNow(): Promise<void> {
    await this.flush();
  }

  public exportLogs(format: 'json' | 'csv' = 'json'): string {
    const allLogs = [...this.buffer, ...this.retryQueue];
    
    if (format === 'json') {
      return JSON.stringify(allLogs, null, 2);
    }
    
    // CSV format
    if (allLogs.length === 0) {
      return 'No logs available';
    }
    
    const headers = ['timestamp', 'level', 'message', 'url', 'userId', 'tenantId'];
    const csv = [
      headers.join(','),
      ...allLogs.map(log => [
        new Date(log.timestamp).toISOString(),
        log.level,
        `"${log.message.replace(/"/g, '""')}"`,
        log.metadata.url,
        log.metadata.userId || '',
        log.metadata.tenantId || ''
      ].join(','))
    ].join('\n');
    
    return csv;
  }

  public clearLogs(): void {
    this.buffer = [];
    this.retryQueue = [];
  }

  private getCurrentTenant(): string {
    return sessionStorage.getItem('tenant') || 'default';
  }

  public destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    
    // Final flush
    this.flush().catch(() => {
      // Silently handle final flush errors
    });
  }
}

// Global logger instance
let globalLogger: Logger | null = null;

export const initializeLogger = (config?: Partial<LoggerConfig>): Logger => {
  if (globalLogger) {
    globalLogger.destroy();
  }
  
  globalLogger = new Logger(config);
  return globalLogger;
};

export const getLogger = (): Logger => {
  if (!globalLogger) {
    globalLogger = new Logger();
  }
  return globalLogger;
};

// Convenience functions for global logger
export const log = {
  debug: (message: string, context?: Record<string, unknown>) => 
    getLogger().debug(message, context),
  
  info: (message: string, context?: Record<string, unknown>) => 
    getLogger().info(message, context),
  
  warn: (message: string, context?: Record<string, unknown>) => 
    getLogger().warn(message, context),
  
  error: (message: string, error?: Error, context?: Record<string, unknown>) => 
    getLogger().error(message, error, context),
  
  critical: (message: string, error?: Error, context?: Record<string, unknown>) => 
    getLogger().critical(message, error, context),
  
  performance: (operation: string, duration: number, context?: Record<string, unknown>) => 
    getLogger().logPerformance(operation, duration, context),
  
  timeOperation: <T>(
    operation: string,
    fn: () => Promise<T>,
    context?: Record<string, unknown>
  ) => getLogger().timeOperation(operation, fn, context)
};